// TasksApi.js
// Generic Celery Tasks API

import axios from "axios";
import pako from "pako";
import { getCsrfToken, setupCsrfInterceptor } from "../auth/csrfUtils";

// Create axios instance with default config
const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  // Session cookies are required for this API
  withCredentials: true,
});

// Setup CSRF protection for this API instance
setupCsrfInterceptor(api);

export const startTask = async (taskName, params = {}) => {
  const response = await api.post(`/tasks/start`, {
    task_name: taskName,
    params,
  });
  return response;
};

export const getTaskStatus = async (taskId) => {
  const response = await api.get(`/tasks/status/${taskId}`);
  return response;
};

/**
 * Download the result of a completed Celery task.
 *
 * If the task stored its output in S3, pass the status response fields
 * to trigger a direct S3 download (bypasses the server entirely).
 * Otherwise, fetches from the server download endpoint as before.
 *
 * @param {string} taskId - The Celery task ID
 * @param {object} [opts] - Optional S3 download info from the status response
 * @param {string} [opts.s3DownloadUrl] - Presigned S3 GET URL
 * @param {string} [opts.filename] - Filename for the download
 * @returns {object|null} Axios response with blob data, or null if S3 download was triggered
 */
export const downloadTaskResult = async (taskId, { s3DownloadUrl, filename } = {}) => {
  if (s3DownloadUrl) {
    // Download directly from S3 via anchor tag (no CORS issues)
    const a = document.createElement("a");
    a.href = s3DownloadUrl;
    a.download = filename || "download";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return null;
  }
  const response = await api.get(`/tasks/download/${taskId}`, {
    responseType: "blob",
  });
  return response;
};
